import {
  AfterContentInit,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewContainerRef,
  WritableSignal
} from '@angular/core';
import {EventService, Product} from '@spartacus/core';
import {ProductPageDecoratorService} from '../../services/product-page-decorator.service';
import {
  ConfigService,
  DcpBundleTemplate,
  FeatureTogglesService,
  TransactionListCta,
  TransactionType
} from '@dcp/configurator';
import {LeadFormLastOpened, LeadService} from '../../services/lead.service';
import {FormDataTrackingService} from '@app/services/analytics/form-data-tracking.service';
import {Subscription} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {
  ActionTypeSelectStrategyService
} from '@app/services/action-type-select-strategy/action-type-select-strategy.service';
import {LuigiContainer} from '@luigi-project/container';
import {LeadFormLuigiContext, LuigiBasicContext} from '@app/product/lead/lead.model';
import {LuigiLeadService} from '@app/product/lead/luigi-lead.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {CampaignTrackingService} from '@app/services/campaign-tracking.service';
import {EddlFormSubmitEvent} from '@app/services/analytics/EDDL/events/eddl-form-submit-event';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {EddlFormErrorEvent} from '@app/services/analytics/EDDL/events/eddl-form-error-event';
import {PrivateOfferModalComponent} from '@app/product/private-offer/private-offer-modal/private-offer-modal.component';
import {LeadModalComponent} from '@app/product/lead/lead-modal/lead-modal.component';
import {LeadUiType} from '@app/model/template-view.model';

@UntilDestroy()
@Component({
  selector: 'dcp-cta-button',
  templateUrl: './cta-button.component.html',
  styleUrls: ['./cta-button.component.scss']
})
export class CtaButtonComponent implements OnInit, AfterContentInit, OnDestroy {

  @Input() fullWidth = false;
  @Input() whitelistedTransactionTypeCodes: string[];
  @Input() outlineButtons = true;
  @Input() highlightFirstButton = false;
  @Input() disableMargin = false;
  @Input() bundleTemplate: DcpBundleTemplate;
  @Input() smallButtons: boolean = false;
  @Input() slmTransactionType: TransactionListCta;
  @Input() product: Product;
  @Input() isHeroSection: boolean;

  @Input() set buttonUiType(type: LeadUiType) {
    this.checkButton(type);
    this.uiType = type;
  };

  get buttonUiType() {
    return this.uiType;
  }

  transactionType: TransactionType;
  uiType: LeadUiType;
  isDisabled: boolean;

  componentDataSubscription: Subscription = Subscription.EMPTY;
  private transactionTypesInner: TransactionType[];
  private static modalComponentRef: LeadModalComponent | PrivateOfferModalComponent;
  public url: string;

  public container: WritableSignal<LuigiContainer | undefined> = signal<LuigiContainer | undefined>(undefined);
  public formContext: WritableSignal<LeadFormLuigiContext | {}> = signal<LeadFormLuigiContext | {}>({});
  public btnContext: WritableSignal<LuigiBasicContext> = signal<LuigiBasicContext>({});

  public useLeadWebComponent: boolean = false;
  public usePrivateOfferWebComponent: boolean = false;

  public leadModalId: string | WritableSignal<string>;

  @Input() set transactionTypes(tts: TransactionType[]) {
    this.transactionTypesInner = tts;
    this.setTransactionType();
  }

  constructor(
    public actionTypeSelectStrategyService: ActionTypeSelectStrategyService,
    public formDataTrackingService: FormDataTrackingService,
    public cdr: ChangeDetectorRef,
    public leadService: LeadService,
    public productPageDecoratorService: ProductPageDecoratorService,
    public luigiLeadService: LuigiLeadService,
    private applicationRef: ApplicationRef,
    private configService: ConfigService,
    private togglesService: FeatureTogglesService,
    private campaignTrackingService: CampaignTrackingService,
    public eventService: EventService,
    public dcpProductUtilitiesService: DcpProductUtilitiesService,
  ) {
  }

  get transactionTypes(): TransactionType[] {
    return this.transactionTypesInner;
  }

  checkButton(type: LeadUiType) {
    this.url = this.configService.getString(`webComponents.${type === LeadUiType.PO ? 'privateOffer' : 'lead'}FormButton`);
    this.togglesService.isEnabled(type === LeadUiType.PO ? UNLEASH_TOGGLES.usePrivateOfferWebComponent : UNLEASH_TOGGLES.useLeadFormWebComoponent).pipe(
      map((m) => [type, m]),
      untilDestroyed(this),
      tap(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      })
    )
      .subscribe(([leadUiType, isEnabled]) => {
        this[`use${leadUiType}WebComponent`] = isEnabled;
        const contextDetails = this.luigiLeadService.setLuigiContextFromTransactionType(this.slmTransactionType);

        if (isEnabled) {
          this.container.set(document.querySelector(`#dcp-${leadUiType === LeadUiType.PO ? 'private-offer' : 'lead'}-form-button`));
          this.btnContext.set({
            buttonText: contextDetails.buttonText,
            modalId: `modal-${leadUiType === LeadUiType.PO ? 'private-offer' : 'lead'}-form`
          });
          this.formContext.set(contextDetails);

          if (this.product?.code) {
            this.formContext.set({...this.formContext(), productCode: this.product.code})
          }

          // add campaignData, if available and not set via transactionType yet
          if (!(this.formContext() as LeadFormLuigiContext).campaignData && this.campaignTrackingService.getCampaignData() !== null) {
            this.formContext.set({...this.formContext(), campaignData: this.campaignTrackingService.getCampaignData()})
          }

          if (CtaButtonComponent.modalComponentRef === undefined) {
            this.initializeNewLeadForm()
          }
        }
      })
  }

  ngOnInit(): void {
    this.setTransactionType();
    this.isDisabled = this.getDisabledStatusByCode();
  }

  handleModalSubmitSuccess() {
    if (CtaButtonComponent.modalComponentRef) {
      CtaButtonComponent.modalComponentRef = undefined;
      this.initializeNewLeadForm();
    }
  }

  initializeNewLeadForm() {
    // Get the root view container ref of the application by injecting it into the root component
    const rootViewContainerRef = this.applicationRef.components[0].injector.get(ViewContainerRef);
    // Insert the modal component into the root view container
    let componentRef: ComponentRef<LeadModalComponent | PrivateOfferModalComponent>;
    if (this.buttonUiType === LeadUiType.PO) {
      componentRef = rootViewContainerRef.createComponent(PrivateOfferModalComponent);
    } else {
      componentRef = rootViewContainerRef.createComponent(LeadModalComponent);
    }
    // Get the instance of the modal component
    componentRef.instance.context = this.formContext() as LeadFormLuigiContext;
    componentRef.changeDetectorRef.detectChanges();
    CtaButtonComponent.modalComponentRef = componentRef.instance;

    this.leadModalId = componentRef.instance.modalId;
  }

  ngAfterContentInit() {
    this.openLastLeadForm();
    window.addEventListener(`dcp-${this.buttonUiType === LeadUiType.PO ? 'private-offer' : 'lead'}-form:modal:submitSuccess:${this.leadModalId}`, (ev: CustomEvent) => {
      this.triggerEddlFormSubmit(ev?.detail?.leadId!)
      this.handleModalSubmitSuccess();
    })

    window.addEventListener(`dcp-lead-form:modal:submitError:${this.leadModalId}`, (ev: CustomEvent) => {
      this.triggerEddlFormError(ev?.detail?.errorMessage?.error ?? ev?.detail?.errorMessage?.message ?? '')
    })
  }

  triggerEddlFormSubmit(leadId: string) {
    const event: EddlFormSubmitEvent = new EddlFormSubmitEvent();
    event.transactionId = leadId;
    event.name = this.formDataTrackingService.generateFormName(
      this.transactionType.adobeFormType,
      this.product
    );

    event.products = [];

    event.products.push({
      ID: this.dcpProductUtilitiesService.getProductId(this.product),
      name: this.product?.name,
      partnerName: this.product?.vendor?.name
    })

    this.eventService.dispatch(event, EddlFormSubmitEvent);
  }

  triggerEddlFormError(errorMessage: string) {
    const event: EddlFormErrorEvent = new EddlFormErrorEvent();
    event.name = this.formDataTrackingService.generateFormName(
      this.transactionType.adobeFormType,
      this.product
    );

    event.products = [];

    event.products.push({
      ID: this.dcpProductUtilitiesService.getProductId(this.product),
      name: this.product?.name,
      partnerName: this.product?.vendor?.name
    })

    this.eventService.dispatch(event, EddlFormErrorEvent);
  }

  ngOnDestroy(): void {
    this.componentDataSubscription.unsubscribe();
  }

  /**
   * Opens the lead form after the user clicked on the Login link on the lead form.
   */
  openLastLeadForm() {
    const leadFormLastOpened: LeadFormLastOpened = this.leadService.leadFormLastOpened;
    if (leadFormLastOpened) {
      this.executeAction(this.product, leadFormLastOpened.transactionListCta, leadFormLastOpened.transactionType);
    }
  }

  /**
   * Opens the supportForm as a fallback for unknown ctas
   */
  openSupportForm() {
    this.executeAction(null, null, {actionType: 'supportForm'} as TransactionType);
  }

  /**
   * Starts the cta action like open the lead form or the configurator.
   *
   * @param product Current Product
   * @param slmTransactionType Current TransactionListCta
   * @param transactionType optional TransactionType in case it differs from the one in this component.
   */
  executeAction(product: Product, slmTransactionType: TransactionListCta, transactionType?: TransactionType): void {
    const actionTypeContext = this.actionTypeSelectStrategyService.selectStrategy(
      transactionType || this.transactionType,
      product,
      slmTransactionType,
      this.bundleTemplate,
    );
    actionTypeContext.exec();
  }

  getTransactionTypeByCode(transactionCode: string) {
    if (this.transactionTypes) {
      return this.transactionTypes.find(entry => entry.code === transactionCode);
    }
    return undefined;
  }

  setTransactionType() {
    this.transactionType = this.getTransactionTypeByCode(this.slmTransactionType.code);
  }

  getDisabledStatusByCode(): boolean {
    return this.slmTransactionType.disable;
  }

  get canShowCta(): boolean {
    return (!this.whitelistedTransactionTypeCodes || this.whitelistedTransactionTypeCodes?.includes(this.slmTransactionType.code))
      && (this.transactionType?.displayInHeroSection === undefined
        || this.transactionType?.displayInHeroSection
        || this.transactionType?.displayInHeroSection === this.isHeroSection)
  }

  get luigiHtmlId(): string {
    return `dcp-${this.buttonUiType === LeadUiType.PO ? 'private-offer' : 'lead'}-form-button`
  }

  get canShowWebcomponent(): boolean {
    return (
      (this.useLeadWebComponent && this.buttonUiType === LeadUiType.LF) || (this.usePrivateOfferWebComponent && this.buttonUiType === LeadUiType.PO)
    ) && this.transactionType.actionType === 'popup';
  }
}
